import axios from 'axios';

import STORAGE_KEYS from '@/define/storageKeys';
import { outboundLog, inboundLog } from '@/utils/Logger';

const axiosInstance = axios.create({ baseURL: `${process.env.REACT_APP_API_URL}/fo` });

const token = localStorage.getItem(STORAGE_KEYS.TOKEN);
if (token) {
  axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
}

const forceLogout = () => {
  removeAuthToken();
  window.location.replace('/');
  return { success: false };
};

let refreshPromise = null;

const refreshAndRetry = async (config) => {
  if (!refreshPromise) {
    refreshPromise = axiosInstance.post('/auth/token/refresh', { access_token: getAuthToken() }).finally(() => {
      refreshPromise = null;
    });
  }

  const refreshed = await refreshPromise;
  if (!refreshed.success || !refreshed.data?.access_token) {
    return forceLogout();
  }

  setAuthToken(refreshed.data.access_token);
  config.headers.Authorization = `Bearer ${refreshed.data.access_token}`;
  return axiosInstance.request(config);
};

// Request intercept
axiosInstance.interceptors.request.use(
  (config) => {
    outboundLog('API', { method: config.method, url: config.url });
    return config;
  },
  (error) => Promise.reject(error),
);
// Response intercept
axiosInstance.interceptors.response.use(
  async ({ data, headers, config }) => {
    // Returns { headers, data } instead of processed data when handling blob or arraybuffer
    if (data instanceof Blob || data instanceof ArrayBuffer) {
      return { headers, data };
    }

    if (!data.success) {
      if (data.data?.code === 4000) return forceLogout(); // refresh token expired
      if (data.data?.code === 4001) {
        // access token expired - 갱신 후에도 만료면 로그아웃 (재시도 무한 루프 방지)
        if (config._retried) return forceLogout();
        config._retried = true;
        return refreshAndRetry(config);
      }
    }

    inboundLog('API', data, data.success);
    return data; // Returns processed API response data directly
  },
  (error) => {
    return {
      success: false,
      data: { error },
    };
  },
);

export default axiosInstance;

export const setAuthToken = (newToken) => {
  axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
  localStorage.setItem(STORAGE_KEYS.TOKEN, newToken);
};

export const getAuthToken = () => {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
};

export const removeAuthToken = () => {
  delete axiosInstance.defaults.headers.common['Authorization'];
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
};
